import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Plus, ShoppingCart, X, Trash2, ChevronRight } from "lucide-react";
import { getToken } from "@/lib/session";

const STATUS_STYLES = {
  draft:      "bg-slate-100 text-slate-700",
  sent:       "bg-blue-100 text-blue-700",
  received:   "bg-emerald-100 text-emerald-700",
  closed:     "bg-emerald-200 text-emerald-800",
  cancelled:  "bg-red-100 text-red-700",
};

export default function AdminPurchaseOrders() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [pos, sups, mats] = await Promise.all([
        api.get("/purchase-orders"),
        api.get("/suppliers"),
        api.get("/materials"),
      ]);
      setRows(pos.data || []);
      setSuppliers(sups.data || []);
      setMaterials(mats.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? rows : rows.filter((p) => p.status === filter);

  return (
    <div data-testid="admin-purchase-orders" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Inventory</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
            Purchase Orders
          </h1>
        </div>
        <div className="flex gap-2 items-center">
          <Link to="/admin/inventory-forecast" className="text-xs text-[#C9A356] hover:underline">
            View forecast →
          </Link>
          <button onClick={() => setCreating(true)} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="new-po-btn">
            <Plus className="w-4 h-4" /> New PO
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-[#E2E8F0] dark:border-slate-700">
        {["all", "draft", "sent", "received", "closed", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider transition ${
              filter === s
                ? "border-b-2 border-[#C9A356] text-[#0A2540] dark:text-white font-medium"
                : "text-[#475569] dark:text-slate-400 hover:text-[#0A2540]"
            }`}
            data-testid={`po-filter-${s}`}
          >
            {s} {s !== "all" && `(${rows.filter((r) => r.status === s).length})`}
          </button>
        ))}
      </div>

      <div className="card-pb overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] dark:bg-slate-800 text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">PO #</th>
              <th className="text-left px-4 py-2">Supplier</th>
              <th className="text-left px-4 py-2">Items</th>
              <th className="text-right px-4 py-2">Total</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">ETA</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[#94A3B8] italic">
                No purchase orders yet.
              </td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.po_id} onClick={() => setDetail(p)} data-testid={`po-row-${p.po_id}`}
                  className="border-t border-[#E2E8F0] dark:border-slate-700 hover:bg-[#F8FAFC] dark:hover:bg-slate-800 cursor-pointer">
                <td className="px-4 py-2 font-mono text-xs">{p.po_number}</td>
                <td className="px-4 py-2 inline-flex items-center gap-1.5">
                  <ShoppingCart className="w-3 h-3 text-[#C9A356]" /> {p.supplier_name}
                </td>
                <td className="px-4 py-2 text-[#475569] dark:text-slate-400">{p.items?.length || 0}</td>
                <td className="px-4 py-2 text-right tabular-nums font-medium">${(p.total || 0).toLocaleString()}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS_STYLES[p.status]}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-[#475569] dark:text-slate-400">
                  {p.eta ? new Date(p.eta).toLocaleDateString() : "—"}
                </td>
                <td className="px-2"><ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <POModal
          suppliers={suppliers}
          materials={materials}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {detail && (
        <PODetailModal
          po={detail}
          onClose={() => setDetail(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}

function POModal({ suppliers, materials, onClose, onSaved }) {
  const [supplierId, setSupplierId] = useState(suppliers[0]?.supplier_id || "");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    const first = materials[0];
    if (!first) { toast.error("Add materials first"); return; }
    setItems((arr) => [...arr, {
      material_id: first.material_id,
      qty: 1,
      unit_cost: first.cost_usd || 0,
    }]);
  };
  const updItem = (idx, patch) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const rmItem  = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const save = async () => {
    if (!supplierId) { toast.error("Pick supplier"); return; }
    if (!items.length) { toast.error("Add at least one item"); return; }
    setSaving(true);
    try {
      await api.post("/purchase-orders", { supplier_id: supplierId, items });
      toast.success("PO created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const subtotal = items.reduce((s, i) => s + Number(i.qty || 0) * Number(i.unit_cost || 0), 0);
  const cls = "border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-2 py-1 text-sm";

  return (
    <ModalShell title="New purchase order" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Supplier *</label>
          <select className={`${cls} w-full`} value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="po-supplier">
            {suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name} — {s.lead_time_days}d</option>)}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Line items</label>
            <button onClick={addItem} className="text-xs text-[#C9A356] hover:underline" data-testid="po-add-item">+ Add</button>
          </div>
          <div className="space-y-1.5">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_60px_80px_28px] gap-1 items-center" data-testid={`po-item-${i}`}>
                <select className={cls} value={it.material_id}
                        onChange={(e) => {
                          const m = materials.find((x) => x.material_id === e.target.value);
                          updItem(i, { material_id: e.target.value, unit_cost: m?.cost_usd || 0 });
                        }}>
                  {materials.map((m) => <option key={m.material_id} value={m.material_id}>{m.name}</option>)}
                </select>
                <input type="number" min="0" step="0.01" className={cls} value={it.qty}
                       onChange={(e) => updItem(i, { qty: e.target.value })} placeholder="Qty" data-testid={`po-item-qty-${i}`} />
                <input type="number" min="0" step="0.01" className={cls} value={it.unit_cost}
                       onChange={(e) => updItem(i, { unit_cost: e.target.value })} placeholder="Cost" />
                <button onClick={() => rmItem(i)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          {items.length > 0 && (
            <div className="text-right mt-2 text-sm">
              Subtotal <b className="tabular-nums">${subtotal.toFixed(2)}</b>
            </div>
          )}
        </div>
        <button onClick={save} disabled={saving} className="btn-accent text-sm w-full disabled:opacity-50" data-testid="po-save">
          {saving ? "Creating…" : "Create draft PO"}
        </button>
      </div>
    </ModalShell>
  );
}

function PODetailModal({ po, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const transition = async (target) => {
    if (target === "received" && !confirm(`Mark PO ${po.po_number} received? This will increment stock for all lines.`)) return;
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${po.po_id}/status`, { target });
      toast.success(`PO ${target}`);
      onChanged();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Transition failed"); }
    finally { setBusy(false); }
  };

  const downloadPdf = () => {
    const token = encodeURIComponent(getToken() || "");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/purchase-orders/${po.po_id}/pdf?auth=${token}`;
    window.open(url, "_blank");
  };
  const nextByStatus = {
    draft:    [["sent", "Mark as Sent"], ["cancelled", "Cancel PO"]],
    sent:     [["received", "Mark Received (stock++)"], ["cancelled", "Cancel"]],
    received: [["closed", "Close PO"]],
  };
  const options = nextByStatus[po.status] || [];

  return (
    <ModalShell title={`${po.po_number} · ${po.supplier_name}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS_STYLES[po.status]}`}>
            {po.status}
          </span>
          <span className="text-xs text-[#94A3B8]">
            ETA {po.eta ? new Date(po.eta).toLocaleDateString() : "—"}
          </span>
        </div>
        <div className="border border-[#E2E8F0] dark:border-slate-700 rounded-md divide-y divide-[#E2E8F0] dark:divide-slate-700">
          {po.items?.map((it, i) => (
            <div key={i} className="p-2 flex justify-between">
              <span className="text-[#0A2540] dark:text-white">
                {it.material_name} <span className="text-[10px] text-[#94A3B8]">× {it.qty} {it.unit}</span>
                {po.status === "received" && (
                  <span className="text-[10px] text-emerald-600 ml-1.5">✓ received</span>
                )}
              </span>
              <span className="tabular-nums">${(Number(it.qty) * Number(it.unit_cost)).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between font-bold">
          <span>Total</span>
          <span className="tabular-nums">${(po.total || 0).toLocaleString()}</span>
        </div>
        {po.notes && <p className="text-xs text-[#475569] italic">{po.notes}</p>}
        <div className="flex flex-col gap-1.5 pt-2 border-t border-[#E2E8F0] dark:border-slate-700">
          <button onClick={downloadPdf} className="btn-outline-navy text-sm py-1.5" data-testid="po-download-pdf">
            Download PDF
          </button>
          {options.map(([target, label]) => (
            <button key={target} onClick={() => transition(target)} disabled={busy}
                    data-testid={`po-transition-${target}`}
                    className={`text-sm py-1.5 rounded ${
                      target === "cancelled"
                        ? "border border-red-300 text-red-600 hover:bg-red-50"
                        : "btn-accent"
                    } disabled:opacity-50`}>
              {label}
            </button>
          ))}
          {options.length === 0 && (
            <p className="text-xs text-[#94A3B8] italic text-center">No further actions available.</p>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">{title}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
